package com.arena_web.ui.sections.eventdetail.mobile

import androidx.compose.runtime.*
import com.arena_web.core.viewmodel.EventDetailUiState
import com.arena_web.core.viewmodel.EventDetailViewModel
import com.arena_web.core.viewmodel.UserParticipation
import com.arena_web.data.dto.EventDetailDto
import com.arena_web.data.dto.EventParticipantDto
import com.arena_web.data.dto.EventTeamDto
import com.arena_web.ui.components.cards.PlayerCard
import com.arena_web.ui.components.cards.PlayerCardSize
import com.arena_web.ui.components.icons.Icon
import com.arena_web.ui.components.overlay.BottomSheet
import com.arena_web.ui.components.widgets.LoadingSpinner
import com.arena_web.ui.sections.arena.EventStatus
import com.arena_web.ui.sections.eventdetail.EventChatPanel
import com.arena_web.ui.sections.eventdetail.TeamColorBadge
import com.arena_web.ui.localization.LocalAppStrings
import com.arena_web.ui.theme.*
import com.arena_web.ui.utils.*
import com.arena_web.utils.Storage
import com.arena_web.utils.StorageKeys
import com.arena_web.utils.openInMaps
import com.varabyte.kobweb.compose.css.Cursor
import com.varabyte.kobweb.compose.css.FontWeight
import com.varabyte.kobweb.compose.css.Overflow
import com.varabyte.kobweb.compose.foundation.layout.*
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.graphics.Color
import com.varabyte.kobweb.compose.ui.attrsModifier
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.compose.ui.thenIf
import com.varabyte.kobweb.silk.components.graphics.Image
import com.varabyte.kobweb.silk.components.text.SpanText
import org.jetbrains.compose.web.css.*
import org.w3c.dom.HTMLElement
import org.w3c.dom.events.Event
import kotlin.js.Date

/**
 * Mobile event-detail / registration screen — the phone counterpart of the
 * desktop two-column layout in `pages/events/Id.kt`. Figma 8472:293461 (Join),
 * 8859:357097 (Leave), 8859:357690 (Start host), 8602:3622 (empty),
 * 8854:355421 (with Create Team sheet), 8458:422465 (chat sheet).
 *
 * The root sets `--scale: var(--mobile-scale)` so every `s()`-scaled helper —
 * and the reused desktop sub-components (TeamColorBadge, PlayerCard,
 * EventChatPanel) — render against the 390-px Figma baseline instead of the
 * desktop ~0.4 clamp. This is the same trick `BottomSheet` uses.
 */
@Composable
fun EventDetailContentMobile(
    state: EventDetailUiState,
    viewModel: EventDetailViewModel,
    isLoggedIn: Boolean,
    onBack: () -> Unit,
    onJoin: () -> Unit,
    onLeave: () -> Unit,
    onStart: () -> Unit,
    onEdit: () -> Unit,
    onDelete: () -> Unit,
    onCreateTeam: () -> Unit,
    onAddPlayer: () -> Unit,
    onConfirmPlayer: (String) -> Unit,
    onRejectPlayer: (String) -> Unit,
    onRemoveFromEvent: (String) -> Unit,
    onToggleAdmin: (String, Boolean) -> Unit,
    onAssignToTeam: (String, String) -> Unit,
    onViewProfile: (String) -> Unit,
    onDeleteTeam: (String) -> Unit,
    onChatAttachmentClick: () -> Unit,
    // COMPLETED events only — the Match Info / Lineups / Media segmented
    // control state lives in Id.kt (it also drives the page's stadium
    // background on the Lineups tab), and Add Media opens Id.kt's popup.
    activeMatchTab: com.arena_web.ui.sections.eventdetail.MatchCenterTab =
        com.arena_web.ui.sections.eventdetail.MatchCenterTab.MatchInfo,
    onMatchTabSelected: (com.arena_web.ui.sections.eventdetail.MatchCenterTab) -> Unit = {},
    onAddMedia: () -> Unit = {},
    /** Media uploads are a subscription feature — true marks the Add Media
     *  FAB with a PRO+ pill (the tap itself opens the paywall via [onAddMedia]). */
    mediaLocked: Boolean = false,
) {
    val t = LocalAppStrings.current
    var chatOpen by remember { mutableStateOf(false) }
    // Long-pressing a team chip (admins) asks to delete it from the event.
    var teamToDelete by remember { mutableStateOf<EventTeamDto?>(null) }

    // The page is rendered edge-to-edge (no layout gutter) so the banner photo
    // paints full-bleed; the back button is overlaid on the photo by the banner
    // itself, and the content below the banner re-adds its own 16-px gutter.
    Column(
        modifier = Modifier
            .fillMaxSize()
            .styleModifier {
                property("--scale", "var(--mobile-scale)")
                property("min-height", "0")
            }
    ) {
        when {
            state.isLoading -> {
                MobileBackRow(onBack)
                Box(
                    modifier = Modifier.fillMaxWidth().flexGrow(1),
                    contentAlignment = Alignment.Center,
                ) { /* Intentionally empty — no loading indicator (user pref). */ }
            }
            state.error != null || state.event == null -> {
                MobileBackRow(onBack)
                Box(
                    modifier = Modifier.fillMaxWidth().flexGrow(1),
                    contentAlignment = Alignment.Center,
                ) {
                    SpanText(
                        state.error ?: t.eventGenericError,
                        modifier = Modifier.sFontSize(16).color(com.arena_web.ui.theme.ThemeVars.Text2),
                    )
                }
            }
            state.event!!.status.uppercase() == "COMPLETED" -> {
                // COMPLETED — Figma 8682:465139 / 8924:361443 / 9031:71550:
                // header row (back + segmented tabs), then tab content.
                CompletedContentMobile(
                    state = state,
                    activeTab = activeMatchTab,
                    onTabSelected = onMatchTabSelected,
                    onBack = onBack,
                    onOpenChat = { chatOpen = true },
                    onViewProfile = onViewProfile,
                    onRatePlayer = { userId, ratings -> viewModel.ratePlayer(userId, ratings) },
                    onAddMedia = onAddMedia,
                    mediaLocked = mediaLocked,
                    onDeleteMedia = { viewModel.deleteMedia(it) },
                )
            }
            else -> {
                val event = state.event!!
                // Full-bleed hero — back button is overlaid on the photo.
                MobileBanner(
                    state = state,
                    onBack = onBack,
                    onJoin = onJoin,
                    onLeave = onLeave,
                    onStart = onStart,
                    onEdit = onEdit,
                    onDelete = onDelete,
                )

                // Everything below the hero fills the remaining height down to
                // the bottom (paginated grid — the whole screen is one page, no
                // page scroll). Keeps the 16-px gutter the edge-to-edge layout
                // dropped.
                Column(
                    modifier = Modifier
                        .fillMaxWidth()
                        .flexGrow(1)
                        .minHeight(0.px)
                        .sGap(10)
                        .sPaddingTop(12)
                        .sPaddingBottom(8)
                        .styleModifier {
                            property("padding-left", "16px")
                            property("padding-right", "16px")
                            property("box-sizing", "border-box")
                        }
                ) {
                    MobilePlayersHeader(
                        event = event,
                        isAdmin = state.isAdmin,
                        onAddPlayer = onAddPlayer,
                        onCreateTeam = onCreateTeam,
                        onChat = { chatOpen = true },
                        onLongPressTeam = { teamToDelete = it },
                    )

                    val confirmed = event.participants.filter { it.status.uppercase() == "CONFIRMED" }
                    val pending = event.participants.filter { it.status.uppercase() == "PENDING" }
                    val roster = confirmed + pending

                    if (roster.isEmpty()) {
                        Box(
                            modifier = Modifier.fillMaxWidth().flexGrow(1).minHeight(0.px),
                            contentAlignment = Alignment.Center,
                        ) { MobileEmptyState() }
                    } else {
                        MobilePlayersGrid(
                            confirmed = confirmed,
                            pending = pending,
                            event = event,
                            isAdmin = state.isAdmin,
                            onConfirmPlayer = onConfirmPlayer,
                            onRejectPlayer = onRejectPlayer,
                            onRemoveFromEvent = onRemoveFromEvent,
                            onToggleAdmin = onToggleAdmin,
                            onAssignToTeam = onAssignToTeam,
                            onViewProfile = onViewProfile,
                        )
                    }
                }
            }
        }
    }

    // ── Chat sheet (Figma 8458:422465) ──
    if (chatOpen) {
        val event = state.event
        if (event?.chatId != null) {
            BottomSheet(onDismiss = { chatOpen = false }, contentGap = 0, fullHeight = true, sidePaddingPx = 0) {
                EventChatPanel(
                    messages = state.chatMessages,
                    messageText = state.chatMessageText,
                    isLoading = state.chatLoading,
                    onTextChanged = { viewModel.updateChatMessageText(it) },
                    onSend = { viewModel.sendChatMessage() },
                    onTyping = { viewModel.sendChatTyping() },
                    onLoadMore = { viewModel.loadMoreChatMessages() },
                    hasMore = state.chatHasMore,
                    participants = event.participants,
                    sportName = event.sportName ?: "football",
                    userParticipation = state.userParticipation,
                    isPrivateEvent = event.privacy.uppercase() == "PRIVATE",
                    onViewProfile = onViewProfile,
                    onJoinClick = onJoin,
                    pinnedMessage = state.chatPinnedMessage,
                    replyingToMessage = state.chatReplyingTo,
                    editingMessage = state.chatEditing,
                    pendingFile = state.chatPendingFile,
                    uploadingFile = state.chatUploadingFile,
                    onReaction = { messageId, emoji -> viewModel.toggleChatReaction(messageId, emoji) },
                    onReply = { viewModel.startChatReply(it) },
                    onCancelReply = { viewModel.cancelChatReply() },
                    onEdit = { viewModel.startChatEdit(it) },
                    onCancelEdit = { viewModel.cancelChatEdit() },
                    onSubmitEdit = { viewModel.submitChatEdit() },
                    onDelete = { viewModel.deleteChatMessage(it) },
                    onPin = { viewModel.pinChatMessage(it) },
                    onAttachmentClick = onChatAttachmentClick,
                    onSendFile = { viewModel.sendChatMediaMessage() },
                    onCancelFile = { viewModel.clearChatPendingFile() },
                    // Match the global sheet fill instead of the desktop-card gradient.
                    backgroundOverride = "var(--l-s2, #09142d)",
                    modifier = Modifier.fillMaxWidth().flexGrow(1).minHeight(0.px),
                )
            }
        }
    }

    // ── Delete-team confirm (long-press on a team chip) ──
    // Wrapped so the dialog's s()-scaled chrome renders at the mobile baseline
    // rather than the desktop ~0.4 clamp.
    teamToDelete?.let { team ->
        Box(modifier = Modifier.styleModifier { property("--scale", "var(--mobile-scale)") }) {
            com.arena_web.ui.components.events.MatchActionConfirmDialog(
                isOpen = true,
                title = t.eventDeleteTeamQuestion,
                message = t.eventDeleteTeamMessage(team.name),
                confirmText = t.commonDelete,
                iconSrc = AppIcons.Trash,
                iconAccent = "#FF4242",
                isBusy = false,
                busyText = t.eventDeleting,
                onCancel = { teamToDelete = null },
                onConfirm = {
                    onDeleteTeam(team.id)
                    teamToDelete = null
                },
            )
        }
    }
}

// ── Banner ──────────────────────────────────────────────────────────────────

// Standalone back row for the loading/error states. Its position (top + left)
// must match the banner's overlaid back button EXACTLY, otherwise the button
// visibly jumps when the event finishes loading and the hero replaces this row.
@Composable
private fun MobileBackRow(onBack: () -> Unit) {
    Row(
        modifier = Modifier
            .fillMaxWidth()
            .sPaddingBottom(14)
            .styleModifier {
                // Same top/left as the banner overlay (safe-area + s(12), s(12)).
                property("padding-top", "calc(env(safe-area-inset-top, 0px) + ${s(12)})")
                property("padding-left", s(12))
                property("padding-right", s(12))
                property("box-sizing", "border-box")
            },
        verticalAlignment = Alignment.CenterVertically,
    ) {
        CircleIconButton(icon = AppIcons.ChevronBack, onClick = onBack)
    }
}

// Circular icon button used on the hero photo (back / edit / delete). Sits on
// the full page-background composite (solid var(--l-s3, #02040B) + the transparent blue tint,
// matching AppTheme's body) so it reads exactly like the rest of the app over
// any photo.
@Composable
private fun BannerCircleButton(icon: String, iconSize: Int = 18, onClick: () -> Unit) {
    Box(
        modifier = Modifier
            .position(Position.Relative)
            .styleModifier {
                property("border-radius", "50%")
                property("background-color", "var(--arena-bg)")
                property(
                    "background-image",
                    "linear-gradient(0deg, rgba(60,121,241,0) 0%, " +
                        "rgba(60,121,241,0.072) 64%, rgba(60,121,241,0.072) 100%)",
                )
            }
    ) {
        CircleIconButton(icon = icon, iconSize = iconSize, onClick = onClick)
    }
}

@Composable
private fun MobileBanner(
    state: EventDetailUiState,
    onBack: () -> Unit,
    onJoin: () -> Unit,
    onLeave: () -> Unit,
    onStart: () -> Unit,
    onEdit: () -> Unit,
    onDelete: () -> Unit,
) {
    val sportColors = LocalSportColors.current
    val t = LocalAppStrings.current
    val event = state.event ?: return

    val eventStatus = when (event.status.uppercase()) {
        "IN_PROGRESS" -> EventStatus.IN_PROGRESS
        "COMPLETED", "FINISHED" -> EventStatus.COMPLETED
        "CANCELED", "CANCELLED" -> EventStatus.CANCELED
        else -> EventStatus.NEW
    }

    val parsed = Date(event.dateTime)
    val months = arrayOf(
        t.eventMonthJanuary, t.eventMonthFebruary, t.eventMonthMarch, t.eventMonthApril,
        t.eventMonthMay, t.eventMonthJune, t.eventMonthJuly, t.eventMonthAugust,
        t.eventMonthSeptember, t.eventMonthOctober, t.eventMonthNovember, t.eventMonthDecember
    )
    val dateStr = "${parsed.getDate()} ${months[parsed.getMonth()]}"
    val timeStr = "${parsed.getHours().toString().padStart(2, '0')}:${parsed.getMinutes().toString().padStart(2, '0')}"

    val sportLabel = event.customSportName?.takeIf { it.isNotBlank() } ?: event.sportName
    val joined = event.participants.count { it.status.uppercase() == "CONFIRMED" }
    val max = event.maxPlayers
    val playersText = if (max != null && max > 0) t.eventPlayersOfMax(joined, max) else t.eventPlayersCount(joined)

    // Cost chip — mirrors desktop: only shown for a positive fee, trimmed of a
    // trailing ".00".
    val costNumber = event.cost.trim().toDoubleOrNull()
    val showCost = costNumber != null && costNumber > 0.0
    val costFormatted = if (showCost) {
        if (costNumber!! % 1.0 == 0.0) costNumber.toInt().toString() else event.cost.trim()
    } else null

    val showAdminIcons = state.isAdmin && eventStatus == EventStatus.NEW
    val showProBadge = event.isPro
    val showPendingBadge = state.userParticipation == UserParticipation.PENDING

    // Full-bleed hero — the photo fills from the very top of the screen (no
    // insets) edge-to-edge, with rounded bottom corners only. Info sits directly
    // on the photo over a legibility gradient (no card / blur / border). The back
    // button overlays top-left and admin icons top-right; the grouped data is
    // bottom-anchored.
    Column(
        modifier = Modifier
            .fillMaxWidth()
            .flexShrink(0)
            .overflow(Overflow.Hidden)
            .position(Position.Relative)
            .styleModifier {
                property("box-sizing", "border-box")
                property("border-radius", "0 0 ${s(16)} ${s(16)}")
                // Tall hero; photo fills behind the status bar (incl. the
                // safe-area area) and content is bottom-anchored.
                property("min-height", "calc(env(safe-area-inset-top, 0px) + ${s(240)})")
            },
        verticalArrangement = Arrangement.Bottom,
    ) {
        // Photo background — absolute fill from the very top of the screen.
        Box(
            modifier = Modifier
                .position(Position.Absolute)
                .top(0.px).left(0.px).right(0.px).bottom(0.px)
                .styleModifier {
                    property(
                        "background",
                        "url('${event.imageUrl ?: "/icons/event_football_field.png"}') center/cover no-repeat",
                    )
                }
        )
        // Darkening overlay — Figma "Overlay / Partial" (8472:293475): a
        // left-to-right diagonal so text reads over the photo. A uniform top
        // scrim is layered on so the top-right (edit/delete) sits on the same
        // darkness as the top-left (back) — otherwise the circular buttons read
        // differently against a bright vs dark patch of photo.
        Box(
            modifier = Modifier
                .position(Position.Absolute)
                .top(0.px).left(0.px).right(0.px).bottom(0.px)
                .styleModifier {
                    property(
                        "background",
                        "linear-gradient(180deg, rgba(0,0,0,0.5) 0%, rgba(0,0,0,0) 18%), " +
                            "linear-gradient(97.36deg, rgba(0,0,0,0.8) 24.66%, rgba(0,0,0,0) 84.73%)",
                    )
                }
        )

        // Back button overlaid on the photo (top-left), clears the notch. Sits
        // on a page-coloured backing so it reads the same as the rest of the app
        // over any photo (Dark.Background = var(--l-s3, #02040B)).
        Box(
            modifier = Modifier
                .position(Position.Absolute)
                .styleModifier {
                    property("top", "calc(env(safe-area-inset-top, 0px) + ${s(12)})")
                    property("left", s(12))
                    property("z-index", "2")
                }
        ) {
            BannerCircleButton(icon = AppIcons.ChevronBack, onClick = onBack)
        }

        // Admin edit/delete overlaid top-right (NEW events only) — same circular
        // buttons as the back button.
        if (showAdminIcons) {
            Row(
                modifier = Modifier
                    .position(Position.Absolute)
                    .sGap(8)
                    .styleModifier {
                        property("top", "calc(env(safe-area-inset-top, 0px) + ${s(12)})")
                        property("right", s(16))
                        property("z-index", "2")
                    },
                verticalAlignment = Alignment.CenterVertically,
            ) {
                BannerCircleButton(icon = AppIcons.Pencil, onClick = onEdit)
                BannerCircleButton(icon = AppIcons.Trash, onClick = onDelete)
            }
        }

        // ── Info block — directly on the photo, bottom-anchored ──
        Column(
            modifier = Modifier
                .fillMaxWidth()
                .position(Position.Relative)
                .sGap(12)
                .styleModifier {
                    property("z-index", "1")
                    property("padding-left", s(16))
                    property("padding-right", s(16))
                    // Lifted a bit higher off the bottom edge.
                    property("padding-bottom", s(28))
                    property("box-sizing", "border-box")
                },
        ) {
            // Title row — title (ellipsis) + PRO badge.
            Row(modifier = Modifier.fillMaxWidth().sGap(12), verticalAlignment = Alignment.Bottom) {
                SpanText(
                    event.title,
                    modifier = Modifier
                        .flexGrow(1)
                        .sFontSize(18)
                        .fontWeight(FontWeight.SemiBold)
                        .color(AppColors.PrimaryWhite)
                        .styleModifier {
                            property("line-height", s(22))
                            property("letter-spacing", s(0.09))
                            property("white-space", "nowrap")
                            property("overflow", "hidden")
                            property("text-overflow", "ellipsis")
                            property("min-width", "0")
                        },
                )
                if (showProBadge) MobileProBadge(sportColors = sportColors)
            }

            // Main frame — info groups (flex, shrink) + action (pinned).
            Row(
                modifier = Modifier.fillMaxWidth().sGap(12),
                horizontalArrangement = Arrangement.SpaceBetween,
                verticalAlignment = Alignment.Bottom,
            ) {
                Box(
                    modifier = Modifier.styleModifier {
                        property("flex", "1 1 0")
                        property("min-width", "0")
                    }
                ) {
                    Column(modifier = Modifier.fillMaxWidth().sGap(12)) {
                        // Group 1 — time/date, then location (full-width ellipsis).
                        Column(modifier = Modifier.fillMaxWidth().sGap(6)) {
                            Row(modifier = Modifier.sGap(12), verticalAlignment = Alignment.CenterVertically) {
                                MiniInfo(icon = AppIcons.Time, text = timeStr, fontSize = 14)
                                MiniInfo(icon = AppIcons.Calendar, text = dateStr, fontSize = 14)
                            }
                            if (event.address.isNotBlank()) {
                                MiniInfo(
                                    icon = AppIcons.Location,
                                    text = event.address,
                                    fontSize = 14,
                                    fillWidth = true,
                                    onClick = { openInMaps(event.latitude, event.longitude, event.address) },
                                )
                            }
                        }
                        // Group 2 — sport·format·teams, then players·cost.
                        Column(modifier = Modifier.fillMaxWidth().sGap(6)) {
                            Row(modifier = Modifier.sGap(8), verticalAlignment = Alignment.CenterVertically) {
                                MiniInfo(icon = AppIcons.ballSmall(event.sportName), text = sportLabel, fontSize = 12)
                                event.format?.let { fmt ->
                                    MiniDot()
                                    SpanText(
                                        t.eventFormatVs(fmt),
                                        modifier = Modifier.sFontSize(12).color(com.arena_web.ui.theme.ThemeVars.Text2)
                                            .styleModifier { property("white-space", "nowrap") },
                                    )
                                }
                                if (event.isPro) {
                                    MiniDot()
                                    SpanText(
                                        t.eventTeamsCount(event.teams.size),
                                        modifier = Modifier.sFontSize(12).color(com.arena_web.ui.theme.ThemeVars.Text2)
                                            .styleModifier { property("white-space", "nowrap") },
                                    )
                                }
                            }
                            Row(modifier = Modifier.sGap(8), verticalAlignment = Alignment.CenterVertically) {
                                MiniInfo(icon = AppIcons.People, text = playersText, fontSize = 12)
                                if (showCost) {
                                    MiniDot()
                                    MiniInfo(icon = AppIcons.Wallet, text = "€$costFormatted", fontSize = 12)
                                }
                            }
                        }
                    }
                }

                // Action column — pending badge on top, action pill at bottom
                // (matches desktop's SpaceBetween action column).
                Column(
                    modifier = Modifier.styleModifier {
                        property("align-self", "stretch")
                        property("flex-shrink", "0")
                    },
                    horizontalAlignment = Alignment.End,
                    verticalArrangement = Arrangement.SpaceBetween,
                ) {
                    if (showPendingBadge) MobilePendingBadge() else Box {}
                    MobilePrimaryAction(
                        state = state,
                        eventStatus = eventStatus,
                        sportColors = sportColors,
                        onJoin = onJoin,
                        onLeave = onLeave,
                        onStart = onStart,
                    )
                }
            }
        }
    }
}

// PRO badge — phone counterpart of the desktop ProBadge (Figma 8836:329307).
@Composable
private fun MobileProBadge(sportColors: SportColorScheme) {
    val t = LocalAppStrings.current
    Row(
        modifier = Modifier
            .sBorderRadius(100)
            .sPadding(4, 10)
            .sGap(4)
            .flexShrink(0)
            .styleModifier {
                // Page-background composite under the badge's own translucent
                // blue: badge fill (top) over the page blue tint over the var(--l-s3, #02040B)
                // base — matches AppTheme's body, so it reads over any photo.
                property("background-color", "var(--arena-bg)")
                property(
                    "background-image",
                    "linear-gradient(rgba(60,121,241,0.18), rgba(60,121,241,0.18)), " +
                        "linear-gradient(0deg, rgba(60,121,241,0) 0%, " +
                        "rgba(60,121,241,0.072) 64%, rgba(60,121,241,0.072) 100%)",
                )
            },
        verticalAlignment = Alignment.CenterVertically,
    ) {
        Box(
            modifier = Modifier
                .sSize(12)
                .backgroundColor(sportColors.primary)
                .styleModifier {
                    property("mask-image", "url('${AppIcons.Diamond}')")
                    property("-webkit-mask-image", "url('${AppIcons.Diamond}')")
                    property("mask-size", "contain")
                    property("-webkit-mask-size", "contain")
                    property("mask-position", "center")
                    property("-webkit-mask-position", "center")
                    property("mask-repeat", "no-repeat")
                    property("-webkit-mask-repeat", "no-repeat")
                }
        )
        SpanText(
            t.eventProBadge,
            modifier = Modifier.sFontSize(12).fontWeight(FontWeight.SemiBold).color(sportColors.primary),
        )
    }
}

// Compact "Pending approval" pill — phone counterpart of the desktop badge.
@Composable
private fun MobilePendingBadge() {
    val t = LocalAppStrings.current
    Row(
        modifier = Modifier
            .sBorderRadius(100)
            .sPadding(4, 10)
            .flexShrink(0)
            .styleModifier {
                property("background-color", "rgba(255, 184, 0, 0.12)")
                property("border", "1px solid rgba(255, 184, 0, 0.45)")
                property("box-sizing", "border-box")
                property("white-space", "nowrap")
            }
            .sGap(6),
        verticalAlignment = Alignment.CenterVertically,
    ) {
        Box(
            modifier = Modifier
                .sSize(6)
                .borderRadius(50.percent)
                .styleModifier { property("background-color", "#FFB800") }
        )
        SpanText(
            t.eventStatusPending,
            modifier = Modifier.sFontSize(12).fontWeight(FontWeight.Medium).color(Color.rgb(255, 216, 107)),
        )
    }
}

@Composable
private fun MobilePrimaryAction(
    state: EventDetailUiState,
    eventStatus: EventStatus,
    sportColors: SportColorScheme,
    onJoin: () -> Unit,
    onLeave: () -> Unit,
    onStart: () -> Unit,
) {
    val t = LocalAppStrings.current
    when {
        eventStatus == EventStatus.IN_PROGRESS -> {
            Row(modifier = Modifier.sGap(6), verticalAlignment = Alignment.CenterVertically) {
                Box(modifier = Modifier.sSize(8).borderRadius(50.percent).backgroundColor(Color.rgb(242, 85, 85)))
                SpanText(t.eventStatusLive, modifier = Modifier.sFontSize(14).color(com.arena_web.ui.theme.ThemeVars.Text1))
            }
        }
        eventStatus == EventStatus.COMPLETED -> {
            Box(
                modifier = Modifier.sHeight(36).sBorderRadius(18).sPadding(8, 18).backgroundColor(Color.rgb(52, 213, 36)),
                contentAlignment = Alignment.Center,
            ) { SpanText(t.eventCompletedBadge, modifier = Modifier.sFontSize(14).color(Color.rgb(0, 0, 0))) }
        }
        eventStatus != EventStatus.NEW -> Unit
        state.isAdmin -> {
            // Admins can't normally leave (the event needs an admin). When a
            // co-admin exists to inherit it, surface Leave under Start so the
            // user can hand it off — same gating as desktop.
            val currentUserId = Storage.get(StorageKeys.USER_ID)
            val hasOtherAdmin = state.event?.participants
                ?.any { it.isAdmin && it.userId != currentUserId } == true
            MobileActionPill(
                text = t.eventStart,
                bg = sportColors.primary,
                textColor = sportColors.onPrimary,
                trailingIconBg = sportColors.secondary,
                trailingIcon = AppIcons.ArrowUpOutline,
                iconTransform = "rotate(45deg)",
                iconFilter = sportColors.iconFilterOnSecondary,
                onClick = onStart,
            )
            if (hasOtherAdmin) {
                MobileActionPill(
                    text = t.eventLeave,
                    bg = Color.rgb(242, 85, 85),
                    textColor = Color.rgb(255, 255, 255),
                    trailingIconBg = Color.rgb(200, 60, 60),
                    trailingIcon = AppIcons.Enter,
                    iconTransform = "rotate(180deg)",
                    iconFilter = "brightness(0) invert(1)",
                    onClick = onLeave,
                )
            }
        }
        state.userParticipation == UserParticipation.CONFIRMED ||
            state.userParticipation == UserParticipation.PENDING -> {
            MobileActionPill(
                text = t.eventLeave,
                bg = Color.rgb(242, 85, 85),
                textColor = Color.rgb(255, 255, 255),
                trailingIconBg = Color.rgb(200, 60, 60),
                trailingIcon = AppIcons.Enter,
                iconTransform = "rotate(180deg)",
                iconFilter = "brightness(0) invert(1)",
                onClick = onLeave,
            )
        }
        else -> {
            MobileActionPill(
                text = t.eventJoin,
                bg = sportColors.primary,
                textColor = sportColors.onPrimary,
                trailingIconBg = sportColors.secondary,
                trailingIcon = AppIcons.ArrowUpOutline,
                iconTransform = "rotate(45deg)",
                iconFilter = sportColors.iconFilterOnSecondary,
                onClick = onJoin,
            )
        }
    }
}

@Composable
private fun MobileActionPill(
    text: String,
    bg: Color,
    textColor: Color,
    trailingIconBg: Color,
    trailingIcon: String,
    iconTransform: String,
    iconFilter: String,
    onClick: () -> Unit,
) {
    Row(
        modifier = Modifier
            .sHeight(36)
            .sBorderRadius(100)
            .backgroundColor(bg)
            .cursor(Cursor.Pointer)
            .onClick { onClick() }
            .sPaddingLeft(16)
            .sPaddingRight(4)
            .sPaddingTop(4)
            .sPaddingBottom(4)
            .sGap(8),
        verticalAlignment = Alignment.CenterVertically,
    ) {
        SpanText(
            text,
            modifier = Modifier
                .sFontSize(14)
                .fontWeight(FontWeight.Medium)
                .color(textColor)
                .styleModifier { property("white-space", "nowrap") },
        )
        Box(
            modifier = Modifier
                .sSize(28)
                .sBorderRadius(100)
                .overflow(Overflow.Hidden)
                .backgroundColor(trailingIconBg),
            contentAlignment = Alignment.Center,
        ) {
            Icon(
                src = trailingIcon,
                modifier = Modifier.sSize(14).styleModifier {
                    property("transform", iconTransform)
                    property("filter", iconFilter)
                },
            )
        }
    }
}

@Composable
private fun MiniInfo(icon: String, text: String, fontSize: Int, fillWidth: Boolean = false, onClick: (() -> Unit)? = null) {
    Row(
        modifier = (if (fillWidth) Modifier.fillMaxWidth() else Modifier).sGap(6)
            .thenIf(onClick != null) { Modifier.cursor(Cursor.Pointer).onClick { onClick!!() } },
        verticalAlignment = Alignment.CenterVertically,
    ) {
        Icon(src = icon, modifier = Modifier.sSize(16).flexShrink(0))
        SpanText(
            text,
            modifier = Modifier.sFontSize(fontSize).color(com.arena_web.ui.theme.ThemeVars.Text2)
                .styleModifier {
                    if (fillWidth) {
                        // Wrap a long address onto a second line, then clamp +
                        // ellipsise past two lines so it never grows the card.
                        property("display", "-webkit-box")
                        property("-webkit-line-clamp", "2")
                        property("-webkit-box-orient", "vertical")
                        property("overflow", "hidden")
                        property("min-width", "0")
                        property("flex", "1 1 0")
                    } else {
                        property("white-space", "nowrap")
                    }
                },
        )
    }
}

@Composable
private fun MiniDot() {
    Box(modifier = Modifier.sSize(4).sBorderRadius(100).backgroundColor(AppColors.Text.Secondary))
}

// ── Region/team chips ─────────────────────────────────────────────────────────

@Composable
private fun MobileRegionChips(
    teams: List<EventTeamDto>,
    canDelete: Boolean = false,
    onLongPressTeam: (EventTeamDto) -> Unit = {},
) {
    Row(
        modifier = Modifier
            .sGap(12)
            .styleModifier {
                // Inside the players-header left column: cap at the column
                // width and scroll the overflow.
                property("max-width", "100%")
                property("overflow-x", "auto")
                property("scrollbar-width", "none")
            },
        verticalAlignment = Alignment.CenterVertically,
    ) {
        teams.forEach { team ->
            Row(
                modifier = Modifier
                    .sGap(6)
                    .then(if (canDelete) Modifier.onLongPress { onLongPressTeam(team) } else Modifier)
                    .styleModifier {
                        if (canDelete) {
                            // Don't let the OS text-selection / callout fire on hold.
                            property("user-select", "none")
                            property("-webkit-user-select", "none")
                            property("-webkit-touch-callout", "none")
                        }
                    },
                verticalAlignment = Alignment.CenterVertically,
            ) {
                TeamColorBadge(color = team.color ?: "#0D184A", size = 23)
                SpanText(
                    team.name.uppercase(),
                    modifier = Modifier
                        .sFontSize(10)
                        .color(com.arena_web.ui.theme.ThemeVars.Text2)
                        .styleModifier {
                            property("letter-spacing", s(0.1))
                            property("white-space", "nowrap")
                        },
                )
            }
        }
    }
}

/** Fires [onLongPress] after a press-and-hold (~500ms). Uses native pointer
 *  events in a ref closure (reliable on mobile); a pointerup/cancel before the
 *  timer — e.g. a scroll — aborts it. */
private fun Modifier.onLongPress(durationMs: Int = 500, onLongPress: () -> Unit): Modifier =
    attrsModifier {
        ref { el ->
            var timer: Int? = null
            val cancel: () -> Unit = {
                timer?.let { kotlinx.browser.window.clearTimeout(it) }
                timer = null
            }
            val down: (org.w3c.dom.events.Event) -> Unit = {
                cancel()
                timer = kotlinx.browser.window.setTimeout({ onLongPress(); timer = null }, durationMs)
            }
            val up: (org.w3c.dom.events.Event) -> Unit = { cancel() }
            el.addEventListener("pointerdown", down)
            el.addEventListener("pointerup", up)
            el.addEventListener("pointercancel", up)
            el.addEventListener("pointerleave", up)
            onDispose {
                cancel()
                el.removeEventListener("pointerdown", down)
                el.removeEventListener("pointerup", up)
                el.removeEventListener("pointercancel", up)
                el.removeEventListener("pointerleave", up)
            }
        }
    }

// ── Players header ────────────────────────────────────────────────────────────

@Composable
private fun MobilePlayersHeader(
    event: EventDetailDto,
    isAdmin: Boolean,
    onAddPlayer: () -> Unit,
    onCreateTeam: () -> Unit,
    onChat: () -> Unit,
    onLongPressTeam: (EventTeamDto) -> Unit,
) {
    val sportColors = LocalSportColors.current
    val t = LocalAppStrings.current
    val confirmed = event.participants.count { it.status.uppercase() == "CONFIRMED" }
    val total = event.participants.size

    Row(
        modifier = Modifier.fillMaxWidth(),
        horizontalArrangement = Arrangement.SpaceBetween,
        verticalAlignment = Alignment.CenterVertically,
    ) {
        // Left group: team chips (Pro events, scrollable) stacked above
        // "Players (x/y)"; the buttons on the right stay centred on the stack.
        Column(
            modifier = Modifier
                .sGap(6)
                .styleModifier {
                    property("flex", "1 1 0")
                    property("min-width", "0")
                },
        ) {
            if (event.isPro && event.teams.isNotEmpty()) {
                MobileRegionChips(
                    teams = event.teams,
                    canDelete = isAdmin,
                    onLongPressTeam = onLongPressTeam,
                )
            }
            Row(modifier = Modifier.sGap(4), verticalAlignment = Alignment.Bottom) {
                SpanText(
                    t.eventPlayersTitle,
                    modifier = Modifier
                        .sFontSize(16)
                        .fontWeight(FontWeight.Medium)
                        .color(com.arena_web.ui.theme.ThemeVars.Text1)
                        .styleModifier {
                            property("line-height", s(18))
                            property("white-space", "nowrap")
                        },
                )
                // Count wrapper carries the 3px bottom padding; the trimmed text box
                // (line-height 12 + text-box-trim) keeps the "(x/y Approved)" baseline
                // aligned with "Players" in the bottom-aligned row (Figma 8859:357844).
                Box(
                    modifier = Modifier.styleModifier {
                        property("display", "flex")
                        property("align-items", "center")
                        property("justify-content", "center")
                        property("padding-bottom", s(3))
                    }
                ) {
                    SpanText(
                        t.eventApprovedCount(confirmed, total),
                        modifier = Modifier
                            .sFontSize(12)
                            .color(Color("#1e7ff5"))
                            .styleModifier {
                                property("line-height", s(12))
                                property("text-box-trim", "trim-both")
                                property("text-box-edge", "cap alphabetic")
                                property("white-space", "nowrap")
                            },
                    )
                }
            }
        }

        Row(
            verticalAlignment = Alignment.CenterVertically,
            modifier = Modifier.sGap(8).styleModifier { property("flex-shrink", "0") },
        ) {
            // "Create Team" CTA — admins on a Pro event only. Non-admins (and
            // non-Pro events) get no "+"; joining is done via the banner button.
            if (isAdmin && event.isPro) {
                Box(
                    modifier = Modifier
                        .sSize(36)
                        .sBorderRadius(100)
                        .backgroundColor(sportColors.primary)
                        .cursor(Cursor.Pointer)
                        .onClick { onCreateTeam() },
                    contentAlignment = Alignment.Center,
                ) {
                    Icon(
                        src = AppIcons.Add,
                        modifier = Modifier.sSize(14).styleModifier {
                            property("filter", sportColors.iconFilterOnPrimary)
                        },
                    )
                }
            }
            CircleIconButton(icon = AppIcons.ChatBubble, iconSize = 16, size = 36, onClick = onChat)
        }
    }
}

// ── Players grid (paginated, 3 columns) ───────────────────────────────────────

private const val PAGE_SIZE = 9 // 3 columns × 3 rows per page

/** Live bridge between the swipe listeners and the current pager state. */
private class PagerSwipe {
    var page = 0
    var pages = 1
    var onPage: (Int) -> Unit = {}
}

// Travel (px) before a horizontal touch counts as a swipe rather than a tap.
private const val SWIPE_ARM = 10.0

/**
 * Wires horizontal swipe-to-page onto the pager [track]. While dragging it
 * writes the track transform directly (so it tracks the finger); on release it
 * flips to the neighbouring page if the drag passed ~20% of the width, otherwise
 * snaps back. Returns a cleanup that detaches the listeners.
 */
private fun wirePagerSwipe(track: HTMLElement, state: PagerSwipe): () -> Unit {
    var startX = 0.0
    var startY = 0.0
    var active = false   // a touch is down
    var armed = false    // moved far enough horizontally to own the gesture
    val base = { "translateX(-${state.page * 100.0}%)" }

    val onStart: (Event) -> Unit = { e ->
        val t = e.asDynamic().touches[0]
        startX = (t.clientX as Number).toDouble()
        startY = (t.clientY as Number).toDouble()
        active = true
        armed = false
    }
    val onMove: (Event) -> Unit = { e ->
        if (active) {
            val t = e.asDynamic().touches[0]
            val dx = (t.clientX as Number).toDouble() - startX
            val dy = (t.clientY as Number).toDouble() - startY
            if (!armed) {
                if (kotlin.math.abs(dx) > SWIPE_ARM && kotlin.math.abs(dx) > kotlin.math.abs(dy)) {
                    armed = true
                    track.style.transition = "none"
                } else if (kotlin.math.abs(dy) > SWIPE_ARM) {
                    active = false // vertical intent — let the page scroll
                }
            }
            if (armed) {
                e.preventDefault()
                // Resist dragging past the first / last page.
                var d = dx
                if ((state.page == 0 && d > 0) || (state.page >= state.pages - 1 && d < 0)) d /= 3
                track.style.transform = "translateX(calc(-${state.page * 100.0}% + ${d}px))"
            }
        }
    }
    val onEnd: (Event) -> Unit = { e ->
        if (active && armed) {
            val t = e.asDynamic().changedTouches[0]
            val dx = (t.clientX as Number).toDouble() - startX
            val width = track.offsetWidth.toDouble().coerceAtLeast(1.0)
            // Restore the snap transition manually — Compose's diff still thinks
            // it set "transform 0.3s ease", so it won't re-apply it after our
            // inline "none" override.
            track.style.transition = "transform 0.3s ease"
            val threshold = width * 0.2
            var next = state.page
            if (dx <= -threshold && state.page < state.pages - 1) next = state.page + 1
            else if (dx >= threshold && state.page > 0) next = state.page - 1
            if (next != state.page) {
                state.onPage(next) // recompose sets the new base transform
            } else {
                track.style.transform = base() // snap back
            }
        }
        active = false
        armed = false
    }

    track.addEventListener("touchstart", onStart)
    track.addEventListener("touchmove", onMove)
    track.addEventListener("touchend", onEnd)
    track.addEventListener("touchcancel", onEnd)
    return {
        track.removeEventListener("touchstart", onStart)
        track.removeEventListener("touchmove", onMove)
        track.removeEventListener("touchend", onEnd)
        track.removeEventListener("touchcancel", onEnd)
    }
}

@Composable
private fun MobilePlayersGrid(
    confirmed: List<EventParticipantDto>,
    pending: List<EventParticipantDto>,
    event: EventDetailDto,
    isAdmin: Boolean,
    onConfirmPlayer: (String) -> Unit,
    onRejectPlayer: (String) -> Unit,
    onRemoveFromEvent: (String) -> Unit,
    onToggleAdmin: (String, Boolean) -> Unit,
    onAssignToTeam: (String, String) -> Unit,
    onViewProfile: (String) -> Unit,
) {
    val roster = confirmed + pending
    val pages = (roster.size + PAGE_SIZE - 1) / PAGE_SIZE
    var page by remember(roster.size) { mutableStateOf(0) }
    // Player action sheet (admin, confirmed players) — assign team / admin / remove.
    var menuFor by remember { mutableStateOf<EventParticipantDto?>(null) }

    // Swipe-to-page: a horizontal drag on the track flips pages, same as the
    // arrows. Native touch listeners drive the track transform directly (mirrors
    // BottomSheet's drag) so it follows the finger; the holder is refreshed every
    // composition so the listeners always read the current page/pages.
    val swipe = remember { PagerSwipe() }
    swipe.page = page
    swipe.pages = pages
    swipe.onPage = { page = it }

    // Warm the image cache for every page up-front so flips are instant. The
    // single-track pager (below) lays every page out side by side, so they all
    // paint once on mount — no per-page warm-up/spread dance needed.
    LaunchedEffect(roster.size) { com.arena_web.ui.components.cards.preloadPlayerCardAssets(roster) }

    Column(modifier = Modifier.fillMaxWidth().flexGrow(1).minHeight(0.px).sGap(2)) {
        // Framed grid block — same decoration as the desktop PlayersGrid: side
        // bracket art (players-frame-bottom.svg) + top/bottom gradient lines
        // (event-chat-container.svg), with pagination chevrons overlaid on the
        // left/right edges (Figma 8472:293624 / 8472:293816). Fills the
        // remaining height; rows are centred vertically inside it.
        Box(modifier = Modifier.fillMaxWidth().flexGrow(1).minHeight(0.px).position(Position.Relative)) {
            // Top + bottom gradient lines (rotated chat-container SVG).
            Box(
                modifier = Modifier
                    .position(Position.Absolute)
                    .top(0.px).left(0.px).right(0.px).bottom(0.px)
                    .fillMaxSize()
                    .styleModifier {
                        property("container-type", "size")
                        property("pointer-events", "none")
                        property("z-index", "0")
                        property("overflow", "hidden")
                    }
            ) {
                Image(
                    src = "/images/event-chat-container.svg",
                    modifier = Modifier.styleModifier {
                        property("position", "absolute")
                        property("top", "0")
                        property("left", "0")
                        property("width", "100cqh")
                        property("height", "100cqw")
                        property("pointer-events", "none")
                        property("transform-origin", "0 0")
                        property("transform", "translateX(100cqw) rotate(90deg)")
                    }
                )
            }

            // Horizontal pager — a SINGLE flex track holds every page side by
            // side and slides as one composited transform. Earlier this stacked
            // the pages absolutely and animated each one's translateX with a
            // warm-up/opacity dance; that flips fine on desktop but stutters (or
            // skips the transition entirely) on mobile, where each per-page layer
            // re-paints under the compositor budget. One moving layer = smooth.
            // The track is `pages × viewport` wide and shifts by whole viewports.
            Box(
                modifier = Modifier
                    .fillMaxSize()
                    .position(Position.Relative)
                    .styleModifier { property("overflow", "hidden"); property("z-index", "1") }
            ) {
                Row(
                    modifier = Modifier
                        .fillMaxHeight()
                        .styleModifier {
                            property("position", "absolute")
                            property("top", "0"); property("left", "0")
                            property("width", "100%")
                            property("flex-wrap", "nowrap")
                            property("will-change", "transform")
                            property("transform", "translateX(-${page * 100.0}%)")
                            property("transition", "transform 0.3s ease")
                            // Let the browser handle vertical scroll but reserve
                            // horizontal panning for our swipe handler.
                            property("touch-action", "pan-y")
                        }
                        .attrsModifier {
                            ref { el ->
                                val cleanup = wirePagerSwipe(el.unsafeCast<HTMLElement>(), swipe)
                                onDispose { cleanup() }
                            }
                        },
                ) {
                    for (p in 0 until pages) {
                        val pageItems = roster.drop(p * PAGE_SIZE).take(PAGE_SIZE)
                        // Each page fills the viewport. `container-type: size`
                        // lives here on a STATIC element (never the animated
                        // track), so the cards size via cqw/cqh without forcing a
                        // container-query recalc on every frame of the slide.
                        Column(
                            modifier = Modifier
                                .fillMaxHeight()
                                .sGap(16)
                                .styleModifier {
                                    property("flex", "0 0 100%")
                                    property("justify-content", "flex-start")
                                    // Centre the grid so height-capped (smaller)
                                    // cards don't leave all the leftover width on
                                    // the right; every row spans the full 3-slot
                                    // grid width, so partial rows still align to
                                    // the grid's left edge. --slot-w is the
                                    // single source for the slot size (also used
                                    // by MobilePlayerSlot).
                                    property("align-items", "center")
                                    property(
                                        "--slot-w",
                                        "min(calc((100cqw - ${s(28)}) / 3), calc((100cqh - ${s(32)}) / 3 * 91 / 142))",
                                    )
                                    property("padding-top", s(20))
                                    property("padding-bottom", s(20))
                                    property("padding-left", s(28))
                                    property("padding-right", s(28))
                                    property("box-sizing", "border-box")
                                    property("container-type", "size")
                                },
                        ) {
                            pageItems.chunked(3).forEach { rowItems ->
                                Row(
                                    modifier = Modifier
                                        .styleModifier {
                                            property("width", "calc(${s(28)} + 3 * var(--slot-w))")
                                            property("justify-content", "flex-start")
                                            property("gap", s(14))
                                        },
                                ) {
                                    rowItems.forEach { participant ->
                                        val isPending = participant.status.uppercase() == "PENDING"
                                        MobilePlayerSlot(
                                            participant = participant,
                                            event = event,
                                            isPending = isPending,
                                            isAdmin = isAdmin,
                                            onConfirm = { onConfirmPlayer(participant.userId) },
                                            onReject = { onRejectPlayer(participant.userId) },
                                            onViewProfile = { onViewProfile(participant.userId) },
                                            onOpenMenu = { menuFor = participant },
                                        )
                                    }
                                }
                            }
                        }
                    }
                }
            }

            // Side bracket art (user-provided Vector.png / Vector-1.png). Each
            // is a full-height vertical bracket whose stroke hugs the inner edge;
            // rendered as a cropped background on a fixed-width strip so the
            // curve sits in the side margin at any grid height.
            Box(
                modifier = Modifier
                    .position(Position.Absolute)
                    .zIndex(1)
                    .sWidth(30)
                    .styleModifier {
                        property("top", "0"); property("bottom", "0"); property("left", "0")
                        property("pointer-events", "none")
                        property("background-image", "url('/images/mobile-players-frame-right.png')")
                        property("background-size", "auto 100%")
                        property("background-repeat", "no-repeat")
                        // Swapped: this image's stroke lives on its left side.
                        property("background-position", "left center")
                    }
            ) {}
            Box(
                modifier = Modifier
                    .position(Position.Absolute)
                    .zIndex(1)
                    .sWidth(30)
                    .styleModifier {
                        property("top", "0"); property("bottom", "0"); property("right", "0")
                        property("pointer-events", "none")
                        property("background-image", "url('/images/mobile-players-frame-left.png')")
                        property("background-size", "auto 100%")
                        property("background-repeat", "no-repeat")
                        // Swapped: this image's stroke lives on its right side.
                        property("background-position", "right center")
                    }
            ) {}

            // Pagination chevrons — hidden entirely at the ends (no dimmed state).
            if (page > 0) {
                Box(
                    modifier = Modifier
                        .position(Position.Absolute)
                        .zIndex(3)
                        .sSize(44)
                        .cursor(Cursor.Pointer)
                        .onClick { page-- }
                        .styleModifier {
                            // Pull out past the 16px content gutter so the arrow
                            // sits flush at the screen edge (no inset).
                            property("left", "-16px"); property("top", "50%")
                            property("transform", "translateY(-50%)")
                        },
                    // Icon hugs the outer (left) edge of the 44px hit area.
                    contentAlignment = Alignment.CenterStart,
                ) { Icon(src = AppIcons.ChevronBack, modifier = Modifier.sSize(24).styleModifier { property("filter", com.arena_web.ui.theme.ThemeVars.ICON_FILTER_V) }) }
            }
            if (page < pages - 1) {
                Box(
                    modifier = Modifier
                        .position(Position.Absolute)
                        .zIndex(3)
                        .sSize(44)
                        .cursor(Cursor.Pointer)
                        .onClick { page++ }
                        .styleModifier {
                            property("right", "-16px"); property("top", "50%")
                            property("transform", "translateY(-50%)")
                        },
                    // Icon hugs the outer (right) edge of the 44px hit area.
                    contentAlignment = Alignment.CenterEnd,
                ) {
                    Icon(
                        src = AppIcons.ChevronBack,
                        modifier = Modifier.sSize(24).styleModifier {
                            property("transform", "rotate(180deg)")
                            property("filter", com.arena_web.ui.theme.ThemeVars.ICON_FILTER_V)
                        },
                    )
                }
            }
        }
    }

    // Admin action sheet for a confirmed player.
    menuFor?.let { participant ->
        MobilePlayerActionSheet(
            participant = participant,
            event = event,
            onDismiss = { menuFor = null },
            onAssignToTeam = { teamId -> onAssignToTeam(participant.userId, teamId); menuFor = null },
            onToggleAdmin = { onToggleAdmin(participant.userId, !participant.isAdmin); menuFor = null },
            onRemove = { onRemoveFromEvent(participant.userId); menuFor = null },
        )
    }
}

@Composable
private fun MobilePlayerSlot(
    participant: EventParticipantDto,
    event: EventDetailDto,
    isPending: Boolean,
    isAdmin: Boolean,
    onConfirm: () -> Unit,
    onReject: () -> Unit,
    onViewProfile: () -> Unit,
    onOpenMenu: () -> Unit,
) {
    val currentUserId = Storage.get(StorageKeys.USER_ID)
    val isClickable = participant.userId != currentUserId
    val displayName = (participant.fullName?.split(" ")?.lastOrNull() ?: participant.username).uppercase()

    // Slot "block" — faint blue wash + gradient hairline border (same as the
    // desktop PlayerSlotCard), with the FIFA card padded inside. Size is the
    // smaller of the width-fit (3 columns, 2 gaps) and height-fit (3 rows, 2
    // gaps) so a full 3×3 page always fits the available area; the aspect-ratio
    // keeps the card shape. The height-fit always divides by 3, so cards stay
    // the SAME size however many rows are shown.
    Box(
        modifier = Modifier
            .sBorderRadius(12)
            .position(Position.Relative)
            .styleModifier {
                property("width", "var(--slot-w)")
                property("aspect-ratio", "91 / 142")
                property("flex-shrink", "0")
                property(
                    "background",
                    "linear-gradient(180deg, rgba(0,0,0,0) 0%, rgba(48,108,206,0.13) 100%)",
                )
                property("box-sizing", "border-box")
                property("overflow", "visible")
            },
    ) {
        // Solid hairline ring — replaces the mask-composite gradient border,
        // which rendered inconsistently at its 0.3px sub-pixel width on the
        // responsive/animated slot. A flat faint-white edge looks the same but
        // never flickers.
        Box(
            modifier = Modifier.styleModifier {
                property("position", "absolute")
                property("inset", "0")
                property("border", "1px solid rgba(255, 255, 255, 0.12)")
                property("border-radius", s(12))
                property("pointer-events", "none")
                property("z-index", "1")
            }
        )

        // Player card (or avatar fallback) padded inside the slot.
        Box(
            modifier = Modifier
                .fillMaxSize()
                .sPaddingTop(14)
                .sPaddingBottom(8)
                .sPaddingLeft(5)
                .sPaddingRight(5)
                .styleModifier { property("box-sizing", "border-box") }
                .then(if (isClickable) Modifier.cursor(Cursor.Pointer).onClick { onViewProfile() } else Modifier),
            contentAlignment = Alignment.TopCenter,
        ) {
            if (participant.cardAssetsBasePath != null) {
                PlayerCard(
                    playerName = displayName,
                    rating = participant.overallRating.toInt(),
                    position = participant.position ?: "",
                    sport = event.sportName,
                    size = PlayerCardSize.Arena,
                    avatarUrl = participant.cardAvatarUrl ?: AppIcons.EmptyAvatar,
                    cardAssetsBasePath = participant.cardAssetsBasePath,
                    cardTextColor = participant.cardTextColor,
                    fillContainer = true,
                )
            } else {
                Column(
                    modifier = Modifier.sGap(6).sPaddingTop(12),
                    horizontalAlignment = Alignment.CenterHorizontally,
                ) {
                    Box(
                        modifier = Modifier
                            .sSize(48)
                            .sBorderRadius(100)
                            .overflow(Overflow.Hidden)
                            .styleModifier { property("background", "rgba(255,255,255,0.1)") },
                        contentAlignment = Alignment.Center,
                    ) {
                        Image(
                            src = participant.avatarUrl ?: AppIcons.EmptyAvatar,
                            modifier = Modifier.fillMaxSize().styleModifier { property("object-fit", "cover") },
                        )
                    }
                    SpanText(
                        displayName,
                        modifier = Modifier
                            .sFontSize(11)
                            .fontWeight(FontWeight.Medium)
                            .color(AppColors.PrimaryWhite)
                            .styleModifier {
                                property("white-space", "nowrap")
                                property("overflow", "hidden")
                                property("text-overflow", "ellipsis")
                                property("max-width", s(80))
                            },
                    )
                }
            }
        }

        // Top-left: team swatch / verified for confirmed. For pending players the
        // amber "awaiting" dot shows only to non-admins — admins instead get the
        // approve/reject buttons in the corners, so the dot would just clash.
        if (!isPending) {
            // Only show the team-colour badge when the player is assigned to a
            // squad; confirmed-but-unassigned players get no top-left badge.
            val assignedTeam = participant.eventTeamId?.let { tid -> event.teams.find { it.id == tid } }
            if (assignedTeam != null) {
                // Smaller badge, placed in the top-left corner exactly like the
                // reject (✕) button: 18px glyph centred in a 44px area at -10/-10.
                Box(
                    modifier = Modifier
                        .position(Position.Absolute)
                        .zIndex(2)
                        .sSize(44)
                        .styleModifier { property("top", s(-10)); property("left", s(-10)) },
                    contentAlignment = Alignment.Center,
                ) {
                    TeamColorBadge(color = assignedTeam.color ?: "#0D184A", size = 18)
                }
            }
        } else if (!isAdmin) {
            Box(
                modifier = Modifier
                    .position(Position.Absolute)
                    .zIndex(2)
                    .sSize(12)
                    .borderRadius(50.percent)
                    .styleModifier {
                        property("top", s(6)); property("left", s(6))
                        // Slightly dimmer amber + pulsing (same keyframe the
                        // desktop pending markers use).
                        property("background-color", "#E0A82E")
                        property("box-shadow", "0 0 6px rgba(224, 168, 46, 0.45)")
                        property("animation", "pp-pending-dot 1.4s ease-in-out infinite")
                    },
            )
            LaunchedEffect(Unit) {
                val styleId = "pp-pending-dot-style"
                if (kotlinx.browser.document.getElementById(styleId) == null) {
                    val styleEl = kotlinx.browser.document.createElement("style")
                    styleEl.id = styleId
                    styleEl.textContent = """
                        @keyframes pp-pending-dot {
                          0%, 100% { opacity: 1;   transform: scale(1); }
                          50%      { opacity: 0.4; transform: scale(0.8); }
                        }
                    """.trimIndent()
                    kotlinx.browser.document.head?.appendChild(styleEl)
                }
            }
        }

        // Top-right: admin settings (confirmed players) — opens the action sheet.
        // Placed like the confirm button: 18px glyph centred in a 44px hit target
        // overhanging the top-right corner (top:-10, right:-10). On the admin's
        // OWN card the only action is "assign to team", so hide the gear entirely
        // when there are no teams.
        val isSelf = participant.userId == currentUserId
        if (!isPending && isAdmin && (!isSelf || event.teams.isNotEmpty())) {
            Box(
                modifier = Modifier
                    .position(Position.Absolute).zIndex(3)
                    .sSize(44)
                    .cursor(Cursor.Pointer)
                    .onClick { it.stopPropagation(); onOpenMenu() }
                    .styleModifier { property("top", s(-10)); property("right", s(-10)) },
                contentAlignment = Alignment.Center,
            ) {
                Icon(src = AppIcons.SettingsAlt, modifier = Modifier.sSize(18))
            }
        }

        // Confirm / reject for pending players (admin only) — Figma 8472:293803:
        // 18px circular buttons (red ✕ top-left, green ✓ top-right) overhanging
        // the card corners by 10px, each inside a 44px hit target.
        if (isPending && isAdmin) {
            Box(
                modifier = Modifier
                    .position(Position.Absolute)
                    .zIndex(3)
                    .styleModifier { property("top", s(-10)); property("left", s(-10)) },
            ) {
                MobileCornerButton(icon = AppIcons.RejectButton, onClick = onReject)
            }
            Box(
                modifier = Modifier
                    .position(Position.Absolute)
                    .zIndex(3)
                    .styleModifier { property("top", s(-10)); property("right", s(-10)) },
            ) {
                MobileCornerButton(icon = AppIcons.ConfirmButton, onClick = onConfirm)
            }
        }
    }
}

/** Pending action — 18px circular button (pre-coloured SVG) centred in a 44px
 *  hit target, matching Figma 8472:293805 / 8472:293808. */
@Composable
private fun MobileCornerButton(icon: String, onClick: () -> Unit) {
    Box(
        modifier = Modifier
            .sSize(44)
            .cursor(Cursor.Pointer)
            .onClick { it.stopPropagation(); onClick() },
        contentAlignment = Alignment.Center,
    ) {
        Image(src = icon, modifier = Modifier.sSize(18))
    }
}

// ── Empty state ───────────────────────────────────────────────────────────────

@Composable
private fun MobileEmptyState() {
    val t = LocalAppStrings.current
    Column(
        modifier = Modifier.fillMaxWidth().sPaddingTop(24).sGap(18),
        horizontalAlignment = Alignment.CenterHorizontally,
    ) {
        Icon(
            src = AppIcons.TeamPeople,
            modifier = Modifier.sWidth(120).sHeight(84).styleModifier {
                property("opacity", "0.15")
                property("filter", "brightness(0) invert(1)")
            },
        )
        Column(
            modifier = Modifier.sGap(12),
            horizontalAlignment = Alignment.CenterHorizontally,
        ) {
            SpanText(
                t.eventAwaitingPlayers,
                modifier = Modifier
                    .sFontSize(24)
                    .fontWeight(FontWeight.Medium)
                    .color(com.arena_web.ui.theme.ThemeVars.Text2)
                    .styleModifier { property("line-height", s(32)); property("letter-spacing", s(0.24)) },
            )
            SpanText(
                t.eventAwaitingPlayersSubtitle,
                modifier = Modifier
                    .sFontSize(16)
                    .color(com.arena_web.ui.theme.ThemeVars.Text2)
                    .styleModifier { property("text-align", "center") },
            )
        }
    }
}

// ── Player action sheet (admin) ───────────────────────────────────────────────

@Composable
private fun MobilePlayerActionSheet(
    participant: EventParticipantDto,
    event: EventDetailDto,
    onDismiss: () -> Unit,
    onAssignToTeam: (String) -> Unit,
    onToggleAdmin: () -> Unit,
    onRemove: () -> Unit,
) {
    val t = LocalAppStrings.current
    val currentUserId = Storage.get(StorageKeys.USER_ID)
    val isSelf = participant.userId == currentUserId
    val displayName = participant.fullName ?: participant.username

    BottomSheet(onDismiss = onDismiss, contentGap = 16) {
        SpanText(
            displayName,
            modifier = Modifier
                .sFontSize(20)
                .fontWeight(FontWeight.Medium)
                .color(com.arena_web.ui.theme.ThemeVars.Text2),
        )

        if (event.teams.isNotEmpty()) {
            SpanText(
                t.eventAssignToTeam,
                modifier = Modifier.sFontSize(14).color(Color("#99b1bf")),
            )
            event.teams.forEach { team ->
                val isCurrent = participant.eventTeamId == team.id
                Row(
                    modifier = Modifier
                        .fillMaxWidth()
                        .sBorderRadius(12)
                        .sPadding(10, 12)
                        .cursor(Cursor.Pointer)
                        .onClick { onAssignToTeam(team.id) }
                        .sGap(10)
                        .then(
                            if (isCurrent) Modifier.styleModifier { property("background", "rgba(0, 97, 255, 0.16)") }
                            else Modifier
                        ),
                    verticalAlignment = Alignment.CenterVertically,
                ) {
                    TeamColorBadge(color = team.color ?: "#0D184A", size = 28)
                    SpanText(
                        team.name,
                        modifier = Modifier.sFontSize(16).fontWeight(FontWeight.Medium)
                            .color(AppColors.PrimaryWhite).flexGrow(1),
                    )
                    if (isCurrent) Icon(src = AppIcons.Check, modifier = Modifier.sSize(16))
                }
            }
        }

        if (!isSelf) {
            ActionSheetRow(
                icon = AppIcons.Shield,
                label = if (participant.isAdmin) t.eventRemoveAdmin else t.eventMakeAdmin,
                onClick = onToggleAdmin,
            )
            Row(
                modifier = Modifier
                    .fillMaxWidth()
                    .sBorderRadius(12)
                    .sPadding(12, 12)
                    .cursor(Cursor.Pointer)
                    .onClick { onRemove() }
                    .sGap(10),
                verticalAlignment = Alignment.CenterVertically,
            ) {
                Box(
                    modifier = Modifier.sSize(18).styleModifier {
                        property("background-color", "#FF5050")
                        property("-webkit-mask", "url('${AppIcons.Trash}') center / contain no-repeat")
                        property("mask", "url('${AppIcons.Trash}') center / contain no-repeat")
                    }
                )
                SpanText(
                    t.eventRemoveFromEvent,
                    modifier = Modifier.sFontSize(16).fontWeight(FontWeight.Medium).color(Color.rgb(255, 80, 80)),
                )
            }
        }
    }
}

@Composable
private fun ActionSheetRow(icon: String, label: String, onClick: () -> Unit) {
    Row(
        modifier = Modifier
            .fillMaxWidth()
            .sBorderRadius(12)
            .sPadding(12, 12)
            .cursor(Cursor.Pointer)
            .onClick { onClick() }
            .sGap(10),
        verticalAlignment = Alignment.CenterVertically,
    ) {
        Icon(
            src = icon,
            modifier = Modifier.sSize(18).styleModifier { property("filter", "brightness(0) invert(1)") },
        )
        SpanText(
            label,
            modifier = Modifier.sFontSize(16).fontWeight(FontWeight.Medium).color(AppColors.PrimaryWhite),
        )
    }
}

// ── Shared circular icon button ───────────────────────────────────────────────

@Composable
internal fun CircleIconButton(
    icon: String,
    iconSize: Int = 18,
    size: Int = 44,
    iconTransform: String? = null,
    dimmed: Boolean = false,
    onClick: () -> Unit,
) {
    Box(
        modifier = Modifier
            .sSize(size)
            .sBorderRadius(100)
            .position(Position.Relative)
            .cursor(Cursor.Pointer)
            .onClick { onClick() }
            .styleModifier {
                // Original faint-blue fill — unchanged, single layer so the
                // colour stays exactly as before.
                property(
                    "background",
                    "linear-gradient(24deg, rgba(0, 59, 174, 0.08) 27%, rgba(3, 87, 230, 0.08) 68%)",
                )
                if (dimmed) property("opacity", "0.4")
            },
        contentAlignment = Alignment.Center,
    ) {
        // Gradient border drawn as a separate ring overlay (mask-composite) so
        // it doesn't sit behind / tint the translucent fill — Figma 8854:354039.
        Box(
            modifier = Modifier.styleModifier {
                property("position", "absolute")
                property("inset", "0")
                property("border", "0.3px solid transparent")
                property("border-radius", "100px")
                property("background", "${AppGradients.ButtonBorderGradientPrimary} border-box")
                property("-webkit-mask", "linear-gradient(#fff 0 0) padding-box, linear-gradient(#fff 0 0)")
                property("mask", "linear-gradient(#fff 0 0) padding-box, linear-gradient(#fff 0 0)")
                property("-webkit-mask-composite", "xor")
                property("mask-composite", "exclude")
                property("pointer-events", "none")
            }
        )
        Icon(
            src = icon,
            modifier = Modifier.sSize(iconSize).styleModifier {
                if (iconTransform != null) property("transform", iconTransform)
            },
        )
    }
}
